import { Alert, Box, MenuItem, Stack, TextField, Typography } from '@mui/material';

import type { ButtonVariantStyle, ShapeScale, SiteThemeButtonStyle } from '@/types/database';
import { PreviewCard } from './PreviewCard';
import { TwoColumn } from './TwoColumn';
import type { SiteStylesScreenProps } from './types';

const SHAPE_OPTIONS: { value: ShapeScale; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'soft', label: 'Soft' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'pill', label: 'Pill' },
];

const VARIANT_OPTIONS: { value: ButtonVariantStyle; label: string }[] = [
  { value: 'contained', label: 'Filled' },
  { value: 'outlined', label: 'Outlined' },
  { value: 'text', label: 'Text' },
];

const SLOTS: { key: 'primary' | 'secondary' | 'tertiary'; label: string }[] = [
  { key: 'primary', label: 'Primary' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'tertiary', label: 'Tertiary' },
];

export function ButtonsScreen({ draft, updateTheme }: SiteStylesScreenProps) {
  const { buttons } = draft.theme_config;

  const setShape = (shape: ShapeScale) =>
    updateTheme({ ...draft.theme_config, buttons: { ...buttons, shape } }, { immediate: true });

  const setSlot = (key: (typeof SLOTS)[number]['key'], patch: Partial<SiteThemeButtonStyle>) =>
    updateTheme({ ...draft.theme_config, buttons: { ...buttons, [key]: { ...buttons[key], ...patch } } }, { immediate: true });

  return (
    <Stack spacing={2.5} sx={{ p: 2.5 }}>
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
          Buttons
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Shape applies to all buttons; style is set per role.
        </Typography>
      </Box>

      <TwoColumn
        controls={
          <>
            <TextField
              select
              label="Shape"
              size="small"
              value={buttons.shape}
              onChange={(event) => setShape(event.target.value as ShapeScale)}
              fullWidth
            >
              {SHAPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {SLOTS.map(({ key, label }) => (
              <Box key={key}>
                <TextField
                  select
                  label={label}
                  size="small"
                  value={buttons[key].variant}
                  onChange={(event) => setSlot(key, { variant: event.target.value as ButtonVariantStyle })}
                  fullWidth
                >
                  {VARIANT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                {key === 'primary' && buttons.primary.variant === 'text' ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    A text-style Primary button is easy for visitors to miss. Filled or Outlined reads more clearly as
                    the main call to action.
                  </Alert>
                ) : null}
              </Box>
            ))}
          </>
        }
        preview={
          <PreviewCard themeConfig={draft.theme_config}>
            <Stack direction="row" sx={{ justifyContent: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              {SLOTS.map(({ key }) => (
                <PreviewButton key={key} variant={buttons[key].variant} shape={buttons.shape} />
              ))}
            </Stack>
          </PreviewCard>
        }
      />
    </Stack>
  );
}

function PreviewButton({ variant, shape }: { variant: ButtonVariantStyle; shape: ShapeScale }) {
  const borderRadius = shape === 'square' ? 0 : shape === 'soft' ? 0.5 : shape === 'pill' ? 999 : 1.25;

  return (
    <Box
      component="button"
      type="button"
      sx={(theme) => ({
        border: variant === 'outlined' ? '1px solid' : 'none',
        borderColor: 'primary.main',
        bgcolor: variant === 'contained' ? 'primary.main' : 'transparent',
        color: variant === 'contained' ? 'primary.contrastText' : 'primary.main',
        borderRadius,
        px: 2.5,
        py: 1,
        fontFamily: theme.typography.button.fontFamily,
        fontSize: 14,
        cursor: 'default',
      })}
    >
      Button
    </Box>
  );
}
